'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import type { SectionOf } from '@/lib/home-v2/schema';
import { SectionHeading, WRAP, fmt, type Labels } from './ui';

/** Links in the old home_page_config sometimes lack the leading slash, make them absolute. */
export function absHref(h?: string) {
  if (!h) return '#';
  return /^(https?:|\/|#|mailto:|tel:)/.test(h) ? h : `/${h}`;
}

/** `shop-by-category` in the existing home_page_config format, drawn in the v2 style. */
export function LegacyShopByCategory({ data, num, labels }: { data: SectionOf<'shop-by-category'>; num: string; labels: Labels }) {
  const [active, setActive] = useState(0);
  const item = data.items[active] ?? data.items[0];
  const viewAll = absHref(item.link?.href ?? item.handle ?? item.id);
  return (
    <section className={`${WRAP} pt-12 md:pt-20`}>
      <SectionHeading num={num} title={data.heading} subtitle={data.description} />
      <div role="tablist" className="scrollbar-hide mt-6 flex gap-2 overflow-x-auto md:pl-[var(--hv-indent)]">
        {data.items.map((it, k) => (
          <button key={it.id} role="tab" aria-selected={k === active} onClick={() => setActive(k)}
            className={`whitespace-nowrap rounded-[10px] px-4 py-2 text-sm ${k === active ? 'bg-hv-orange font-semibold text-white' : 'border border-hv-line'}`}>
            {it.title}{it.subtitle ? <span className="ml-1.5 font-hv-mono text-[11px] opacity-70">{it.subtitle}</span> : null}
          </button>
        ))}
      </div>
      <ul className="scrollbar-hide -mx-4 mt-5 flex gap-3 overflow-x-auto px-4 md:mx-0 md:grid md:grid-cols-5 md:gap-4 md:overflow-visible md:px-0 md:pl-[var(--hv-indent)]">
        {item.children.map((c) => (
          <li key={c.id} className="w-[130px] flex-shrink-0 md:w-auto">
            <Link href={absHref(c.handle)} className="group flex flex-col gap-2">
              <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-hv-card">
                {c.image && <Image src={c.image} alt={c.title} fill sizes="(min-width: 768px) 220px, 130px" className="object-cover transition duration-500 group-hover:scale-105" />}
              </div>
              <span className="text-[13px] font-semibold leading-tight md:text-[15px]">{c.title}</span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-4 md:pl-[var(--hv-indent)]"><Link href={viewAll} className="font-semibold text-hv-orange hover:underline">{fmt(labels.viewAllNamed, { name: item.title })} →</Link></div>
    </section>
  );
}

/** `trending-now` in the existing home_page_config format, drawn in the v2 style. */
export function LegacyTrending({ data, num }: { data: SectionOf<'trending-now'>; num: string }) {
  return (
    <section className={`${WRAP} pt-12 md:pt-20`}>
      <SectionHeading num={num} title={data.heading} subtitle={data.description} />
      <ul className="scrollbar-hide -mx-4 mt-6 flex gap-3 overflow-x-auto px-4 md:mx-0 md:mt-8 md:grid md:grid-cols-4 md:gap-5 md:overflow-visible md:px-0 md:pl-[var(--hv-indent)]">
        {data.items.map((it) => (
          <li key={it.id} className="w-[200px] flex-shrink-0 md:w-auto">
            <Link href={absHref(it.link?.href)} target={it.link?.openInNewTab ? '_blank' : undefined} className="group block">
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-hv-card">
                <Image src={it.src} alt={it.alt || it.title || ''} fill sizes="(min-width: 768px) 320px, 200px" className="object-cover transition duration-500 group-hover:scale-105" />
                {it.badge && <span className="absolute left-3 top-3 rounded-md bg-hv-orange px-2 py-1 font-hv-mono text-[11px] text-white">{it.badge}</span>}
              </div>
              {it.title && <div className="mt-2.5 font-semibold">{it.title}</div>}
              {it.subtitle && <div className="text-sm text-hv-soft">{it.subtitle}</div>}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
